import { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

interface WindButtonProps {
  onWind: () => void;
}

const Container = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  z-index: 1000;
`;

const StyledButton = styled(motion.button)`
  background: linear-gradient(145deg, #f0f9ff 0%, #e0f2fe 40%, #bae6fd 100%);
  border: 2px solid rgba(147, 197, 253, 0.65);
  border-radius: 50px;
  padding: 10px 18px 10px 12px;
  cursor: pointer !important;
  display: flex;
  align-items: center;
  gap: 9px;
  box-shadow: 0 4px 18px rgba(14, 116, 144, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.9);
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #0369a1;
  outline: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  border: none;
  border: 2px solid rgba(147, 197, 253, 0.65);
`;

const windLineVariants = {
  idle: { pathLength: 1 as number, opacity: 0.45 },
  active: (delay: number) => ({
    pathLength: [0, 1] as number[],
    opacity: [0, 1, 0] as number[],
    transition: {
      duration: 0.55,
      delay: delay * 0.14,
      repeat: Infinity,
      ease: 'easeInOut' as const,
    },
  }),
};

const buttonVariants = {
  idle: {
    boxShadow: '0 4px 18px rgba(14, 116, 144, 0.28)',
  },
  blowing: {
    boxShadow: [
      '0 4px 18px rgba(14, 116, 144, 0.28)',
      '0 8px 32px rgba(14, 116, 144, 0.55)',
      '0 4px 18px rgba(14, 116, 144, 0.28)',
    ],
    transition: { duration: 0.9, repeat: 2 },
  },
};

const CloudIcon = ({ isBlowing }: { isBlowing: boolean }) => (
  <svg width="44" height="28" viewBox="0 0 44 28" fill="none">
    {/* Cloud body */}
    <ellipse cx="15" cy="20" rx="12" ry="7" fill="white" opacity="0.97" />
    <circle cx="8" cy="16" r="7" fill="white" opacity="0.97" />
    <circle cx="16" cy="12" r="8.5" fill="white" opacity="0.97" />
    <circle cx="23" cy="15" r="6" fill="white" opacity="0.97" />
    {/* Soft highlight */}
    <ellipse cx="13" cy="11" rx="5" ry="2.5" fill="white" opacity="0.35" />

    {/* Wind lines */}
    <motion.path
      d="M27 14 Q33 12 40 10"
      stroke="#38bdf8"
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
      variants={windLineVariants}
      animate={isBlowing ? 'active' : 'idle'}
      custom={0}
    />
    <motion.path
      d="M27 18.5 Q34 17.5 41 17"
      stroke="#38bdf8"
      strokeWidth="2.5"
      strokeLinecap="round"
      fill="none"
      variants={windLineVariants}
      animate={isBlowing ? 'active' : 'idle'}
      custom={1}
    />
    <motion.path
      d="M27 23 Q32 23.5 38 22.5"
      stroke="#38bdf8"
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
      variants={windLineVariants}
      animate={isBlowing ? 'active' : 'idle'}
      custom={2}
    />
  </svg>
);

export const WindButton = ({ onWind }: WindButtonProps) => {
  const [isBlowing, setIsBlowing] = useState(false);

  const handleClick = () => {
    if (isBlowing) return;
    onWind();
    setIsBlowing(true);
    setTimeout(() => setIsBlowing(false), 2000);
  };

  return (
    <Container>
      <StyledButton
        onClick={handleClick}
        variants={buttonVariants}
        animate={isBlowing ? 'blowing' : 'idle'}
        whileHover={{ scale: 1.06, y: -3 }}
        whileTap={{ scale: 0.92 }}
      >
        <CloudIcon isBlowing={isBlowing} />
        <span style={{ minWidth: 76 }}>
          {isBlowing ? 'Soprando...' : 'Soprar 🌬️'}
        </span>
      </StyledButton>
    </Container>
  );
};
